import { put, call, select, take, fork } from 'redux-saga/effects'
import { pickBy, identity } from 'lodash'

import {
  routeIdSelector,
  waitingPassengersSelector,
  routeNameSelector,
  routeTypeSelector,
  fromUaRouteSelector,
  routeCompanySelector
} from 'reduxModules/selectors/RouteSelectors'
import {
  departurePlaceSagaSelector,
  allCitiesSelector,
  departureTimeSagaSelector
} from 'reduxModules/selectors/CitySelectors'
import { dateSelector } from 'reduxModules/selectors/DateSelectors'
import {
  userIdSelector,
  isPartnerSelector,
  isLimitedDispatcherSelector,
  userNameSelector,
  userRoleSelector,
  isUserWithFullAccessSelector
} from 'reduxModules/selectors/UserSelectors'
import { bookActions } from 'reduxModules/actions/BookActions'
import { citiesPriceSagaSelector } from 'reduxModules/selectors/PriceSelectors';
import { bookingActions } from 'reduxModules/actions/BookingActions'
import { routeActions, routeTypes } from 'reduxModules/actions/RouteActions'
import { sourceActions } from 'reduxModules/actions/SourceActions'
import { priceActions } from 'reduxModules/actions/PriceActions'
import { cityActions } from 'reduxModules/actions/CityActions'
import { createDateId, addDays } from 'services/helpers'
import { handleRouteLimit, handleSourceData, confirmSaga, handleRouteInfo } from 'services/sagaHelpers'
import { snackbarActions } from 'reduxModules/actions/SnackbarActions'
import {
  companySelector,
  isPrexBusSelector,
  isAdminSelector,
  isDispatcherSelector
} from 'reduxModules/selectors/UserSelectors'
import { change, reset } from 'redux-form'

import { createReadableDateTime, createReadableDate } from 'services/helpers'

import { HIDDEN_ROUTES } from 'constants/config'

import { firestore, rsf } from '../../firebase'

export function* getRoutesSaga(action) {
  const { responseSuccess, responseFailure } = action

  try {
    const company = yield select(companySelector)
    const isPrexBus = yield select(isPrexBusSelector)
    const isAdmin = yield select(isAdminSelector)
    const isDispatcher = yield select(isDispatcherSelector)
    const query = isPrexBus && (isAdmin || isDispatcher)
      ? firestore
        .collection('routes')
        .orderBy('order')
      : firestore
        .collection('routes')
        .where("company", "==", company)
        .orderBy('order')

    const res = yield call(
      rsf.firestore.getCollection,
      query
    )

    if (!res.empty) {
      const routes = []

      yield res.forEach(route => {
        if (HIDDEN_ROUTES.includes(route.id)) {
          return
        } else {
          routes.push({ id: route.id, ...route.data() })
        }
      })

      yield put(responseSuccess(routes))
    } else {
      yield put(snackbarActions.showSnackbar({
        type: 'error',
        message: 'Маршрути відсутні!'
      }))
      yield put(responseFailure())
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження маршрутів!'
    }))
  }
}

function* unsubscribeInfoSaga(unsubscribe, data) {
  while (true) {
    yield take(routeTypes.UNSUBSCRIBE_INFO_ATTEMPT)
    unsubscribe.close()
  }
}

export function* subscribeInfoSaga(action) {
  const { responseSuccess, responseFailure } = action

  try {
    const routeId = yield select(routeIdSelector)
    const date = yield select(dateSelector)
    const dateId = createDateId(date)
    const channel = rsf.firestore.channel(`routes/${routeId}/info/${dateId}`, 'document')

    yield fork(unsubscribeInfoSaga, channel, { routeId, dateId })

    while (true) {
      const res = yield take(channel)

      if (res.exists) {
        yield put(change('book', 'limit', res.data().limit))
        yield put(responseSuccess(res.data()))
      } else {
        yield put(responseSuccess({}))
      }
    }
  } catch (err) {
    console.log(err)
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження інформації про маршрут!'
    }))
    yield put(responseFailure())
  }
}

export function* changeRouteLimitSaga(action) {
  const { responseSuccess, responseFailure, data: { target: { value } } } = action

  try {
    yield handleRouteLimit(value)

    yield put(snackbarActions.showSnackbar({
      type: 'success',
      message: 'Ліміт оновлений!'
    }))

    yield put(responseSuccess())
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час оновлення ліміту, будь ласка повторіть спробу'
    }))
  }
}

export function* selectRouteSaga() {
  const routeId = yield select(routeIdSelector)
  const isPartner = yield select(isPartnerSelector)
  const isLimitedDispatcher = yield select(isLimitedDispatcherSelector)
  const fromUaRoute = yield select(fromUaRouteSelector)

  if (routeId) {
    const splitedRouteId = routeId.split('_')

    yield put(routeActions.unsubscribeInfo())
    yield put(bookingActions.unsubscribeBookings())
    yield put(bookingActions.unsubscribeReturnBookings())

    yield put(bookingActions.subscribeBookings())
    yield put(priceActions.clearCitiesRoutePrice())
    yield put(bookActions.clearDepartureTime())
    yield put(sourceActions.getSources(routeId))
    yield put(priceActions.getPrices(routeId))
    yield put(cityActions.getRouteCities(routeId))
    yield put(routeActions.subscribeInfo())
    yield put(routeActions.subscribeWaitingPassengers())

    if ((!isPartner && !isLimitedDispatcher) && fromUaRoute) {
      const returnRouteId = splitedRouteId[1] + '_' + splitedRouteId[0]

      yield put(bookingActions.subscribeReturnBookings(returnRouteId))
    } else {
      yield put(bookingActions.clearReturnBookings())
    }
  } else {
    yield put(routeActions.unsubscribeInfo())
    yield put(bookingActions.unsubscribeBookings())
    yield put(bookingActions.unsubscribeReturnBookings())

    yield put(sourceActions.clearSources())
    yield put(priceActions.clearPrices())
    yield put(cityActions.clearRouteCities())
    yield put(bookingActions.clearBookings())
    yield put(routeActions.unsubscribeWaitingPassengers())

    yield put(change('book', 'limit', 0))
  }
}

export function* setSourceDataSaga(action) {
  const { responseSuccess, responseFailure, data: { name, diff, id, label } } = action

  try {
    yield handleSourceData()

    if (name === 'booked' && diff > 0) {
      yield put(sourceActions.addSourceBooking({ id, label, diff }))

      yield put(snackbarActions.showSnackbar({
        type: 'success',
        message: 'Бронювання з системи добавленно'
      }))
    } else if (name === 'limit') {
      yield put(snackbarActions.showSnackbar({
        type: 'success',
        message: 'Ліміт оновлений!'
      }))
    }

    yield put(responseSuccess())
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час оновлення системи, будь ласка повторіть спробу'
    }))
  }
}

export function* setRouteInfoSaga(action) {
  const { responseSuccess, responseFailure, data: { key, value } } = action

  try {
    const isPartner = yield select(isPartnerSelector)
    const isLimitedDispatcher = yield select(isLimitedDispatcherSelector)
    const date = yield select(dateSelector)
    const routeId = yield select(routeIdSelector)
    const dateId = createDateId(date)
    const updatedDate = addDays(date, 2)
    const returnDateId = createDateId(updatedDate)
    const fromUaRoute = yield select(fromUaRouteSelector)

    yield call(
      rsf.firestore.setDocument,
      `routes/${routeId}/info/${dateId}`,
      { [key]: value },
      { merge: true }
    )

    const splitedRouteId = routeId.split('_')

    if ((!isPartner && !isLimitedDispatcher) && fromUaRoute) {
      const returnRouteId = splitedRouteId[1] + '_' + splitedRouteId[0]

      yield call(
        rsf.firestore.setDocument,
        `routes/${returnRouteId}/info/${returnDateId}`,
        { [key]: value },
        { merge: true }
      )
    }

    yield put(snackbarActions.showSnackbar({
      type: 'success',
      message: 'Інформація оновлена'
    }))

    yield put(responseSuccess())
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час оновлення інформації'
    }))
  }
}

export function* disableRouteSaga(action) {
  const { responseSuccess, responseFailure, data: checked } = action

  try {
    const date = yield select(dateSelector)
    const routeId = yield select(routeIdSelector)
    const dateId = createDateId(date)
    const month = date.getMonth() + 1
    const year = date.getFullYear()
    const routeCompany = yield select(routeCompanySelector)
    const company = yield select(companySelector)
    const isUserWithFullAccess = yield select(isUserWithFullAccessSelector)

    if (!isUserWithFullAccess && routeCompany !== company) {
      yield put(snackbarActions.showSnackbar({
        type: 'error',
        message: 'Доступ відсутній!'
      }))

      yield put(responseFailure(checked))

      return
    }

    yield call(
      rsf.firestore.setDocument,
      `routes/${routeId}/info/${dateId}`,
      { month, year, disabled: checked },
      { merge: true }
    )

    const res = checked ? 'заблокований' : 'розблокаваний'

    yield put(snackbarActions.showSnackbar({
      type: 'success',
      message: `Рeйс ${res}!`
    }))

    yield put(responseSuccess())
  } catch (err) {
    console.log(err)
    yield put(responseFailure(checked))
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час блокування рейсу'
    }))
  }
}

function* unsubscribeWaitingPassengersSaga(unsubscribe, data) {
  while (true) {
    yield take(routeTypes.UNSUBSCRIBE_WAITING_PASSENGERS_ATTEMPT)
    unsubscribe.close()
  }
}

export function* subscribeWaitingPassengersSaga(action) {
  const { responseSuccess, responseFailure } = action

  try {
    const routeId = yield select(routeIdSelector)
    const date = yield select(dateSelector)
    const dateId = createDateId(date)

    const channel = rsf.firestore.channel(`routes/${routeId}/info/${dateId}/waiting_passengers`)

    yield fork(unsubscribeWaitingPassengersSaga, channel, { routeId, dateId })

    while (true) {
      const res = yield take(channel)

      if (!res.empty) {
        const waitingPassengers = []

        yield res.forEach(passenger => waitingPassengers.push({ id: passenger.id, ...passenger.data() }))

        yield put(responseSuccess(waitingPassengers))
      } else {
        yield put(responseSuccess([]))
      }
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час звантаження пасажирів в очікуванні!'
    }))
  }
}

export function* setWaitingPassengersSaga(action) {
  const { responseSuccess, responseFailure, data } = action

  try {
    const routeId = yield select(routeIdSelector)
    const date = yield select(dateSelector)
    const dateId = createDateId(date)

    const createdDate = createReadableDateTime(new Date())
    const userName = yield select(userNameSelector)

    const passengerData = {
      name: data.name,
      phone: data.phone,
      to: data.to.trim(),
      surname: data.surname,
      from: data.from.trim(),
      createdDate,
      userName
    }

    yield call(rsf.firestore.addDocument, `routes/${routeId}/info/${dateId}/waiting_passengers`, passengerData)

    yield put(reset('AddWaitingPassenger'))

    yield put(responseSuccess())
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час добавлення пасажира в список очікування!'
    }))
  }
}

export function* deleteWaitingPassengerSaga(action) {
  const { responseSuccess, responseFailure, data: id } = action

  try {
    const routeId = yield select(routeIdSelector)
    const date = yield select(dateSelector)
    const dateId = createDateId(date)

    const confirmed = yield call(confirmSaga, 'deleteWaitingPassenger')

    if (confirmed) {
      yield call(rsf.firestore.deleteDocument, `routes/${routeId}/info/${dateId}/waiting_passengers/${id}`)

      yield put(snackbarActions.showSnackbar({
        type: 'success',
        message: 'Пасажир видалений!'
      }))
      yield put(responseSuccess())
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час видалення пасажира зі списку очікування!'
    }))
  }
}

export function* addWaitingPassengerToSheetSaga(action) {
  const { responseSuccess, responseFailure, data } = action

  try {
    const allCities = yield select(allCitiesSelector)
    const waitingPassengers = yield select(waitingPassengersSelector)
    const waitingPassenger = waitingPassengers.find(({ id }) => id === data)
    waitingPassenger.id = ''

    const fromCity = allCities.find(({ label }) => label === waitingPassenger.from)
    const toCity = allCities.find(({ label }) => label === waitingPassenger.to)

    const fromToCities = {
      from: fromCity.id,
      to: toCity.id
    }

    const price = yield select(citiesPriceSagaSelector, fromToCities)

    const routeId = yield select(routeIdSelector)
    const routeName = yield select(routeNameSelector)
    const routeType = yield select(routeTypeSelector)

    const date = yield select(dateSelector)
    const dateId = createDateId(date)

    const userRole = yield select(userRoleSelector)
    const userId = yield select(userIdSelector)
    const userName = yield select(userNameSelector)

    const departurePlace = yield select(departurePlaceSagaSelector, fromToCities)
    const departureTime = yield select(departureTimeSagaSelector, fromToCities)
    const departureDate = createReadableDate(date)
    const departureTimestamp = date.valueOf()
    const createdDate = createReadableDateTime(new Date())

    const bookData = {
      ...waitingPassenger,
      routeId,
      routeName,
      routeType,
      userRole,
      userId,
      userName,
      departurePlace,
      departureTime,
      departureDate,
      departureTimestamp,
      createdDate,
      source: userRole,
      price
    }
    const filteredBookData = pickBy(bookData, identity)

    yield call(rsf.firestore.addDocument, `bookings`, filteredBookData)

    yield call(rsf.firestore.deleteDocument, `routes/${routeId}/info/${dateId}/waiting_passengers/${data}`)

    yield handleRouteInfo([filteredBookData])

    yield put(snackbarActions.showSnackbar({
      type: 'success',
      message: 'Пасажир добавлений у відомість!'
    }))
    yield put(responseSuccess())
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час добавлення пасажира в відомість!'
    }))
  }
}


// WEBPACK FOOTER //
// ./src/reduxModules/sagas/RouteSagas.js